/**
 * VideoPlayerModal
 * Full-screen modal for playing self-hosted videos inline
 * Uses React portal to render at document root level
 */

import React, { useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import styles from '../styles/VideoPlayerModal.module.css';

function VideoPlayerModal({ videoUrl, title, onClose }) {
  const videoRef = useRef(null);

  // Detect mobile device
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  useEffect(() => {
    // Prevent body scrolling when modal is open
    document.body.style.overflow = 'hidden';

    // On mobile, request fullscreen and lock to landscape
    const enterFullscreen = async () => {
      if (isMobile && videoRef.current) {
        try {
          // Try to enter fullscreen on the video element
          if (videoRef.current.requestFullscreen) {
            await videoRef.current.requestFullscreen();
          } else if (videoRef.current.webkitEnterFullscreen) {
            // iOS Safari uses this for video fullscreen
            await videoRef.current.webkitEnterFullscreen();
          } else if (videoRef.current.webkitRequestFullscreen) {
            await videoRef.current.webkitRequestFullscreen();
          }

          // Try to lock orientation to landscape
          if (window.screen.orientation?.lock) {
            await window.screen.orientation.lock('landscape').catch(() => {
              // Orientation lock may not be supported or allowed
            });
          }
        } catch (error) {
          console.log('[VideoPlayerModal] Fullscreen/orientation not supported:', error);
        }
      }
    };

    // Auto-play video when modal opens
    if (videoRef.current) {
      videoRef.current.play().catch(error => {
        console.error('[VideoPlayerModal] Video autoplay failed:', error);
      });

      // Enter fullscreen after a short delay (needed for iOS)
      if (isMobile) {
        setTimeout(enterFullscreen, 100);
      }
    }

    // Handle escape key to close
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    // Handle fullscreen exit (user pressed native back/exit)
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement && !document.webkitFullscreenElement) {
        // User exited fullscreen, close the modal
        if (isMobile) {
          onClose();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);

      // Unlock orientation when closing
      if (window.screen.orientation?.unlock) {
        window.screen.orientation.unlock();
      }

      // Exit fullscreen if still active
      if (document.fullscreenElement || document.webkitFullscreenElement) {
        if (document.exitFullscreen) {
          document.exitFullscreen().catch(() => {});
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        }
      }
    };
  }, [onClose, isMobile]);

  const handleOverlayClick = (e) => {
    e.stopPropagation();
    // Close only if clicking the overlay (not the content)
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleContainerClick = (e) => {
    e.stopPropagation();
  };

  const handleClose = (e) => {
    e.stopPropagation();
    onClose();
  };

  const modalContent = (
    <div className={styles.modalOverlay} onClick={handleOverlayClick}>
      <div className={styles.modalContainer} onClick={handleContainerClick}>
        {/* Close button */}
        <button className={styles.closeButton} onClick={handleClose} aria-label="Close video">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        {/* Video title */}
        {title && <h2 className={styles.title}>{title}</h2>}

        {/* Video player */}
        <video
          ref={videoRef}
          className={styles.video}
          src={videoUrl}
          controls
          playsInline
          preload="auto"
        />
      </div>
    </div>
  );

  // Render in portal at document root level
  return ReactDOM.createPortal(modalContent, document.body);
}

export default VideoPlayerModal;
